import React, { useState, useEffect } from "react";
import {
  Typography, Box, Grid, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton
} from "@mui/material";
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector,
  TimelineContent, TimelineDot, TimelineOppositeContent
} from "@mui/lab";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { parseISO, formatISO, format, isValid } from 'date-fns';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { deleteKeyDetail } from "../Dossier/Network_call";

const Events = ({ refreshDossierData, onEventChange, dossierData }) => {
  const [events, setEvents] = useState({ upcoming: [], past: [] });
  const [newEvent, setNewEvent] = useState({ date: "", status: "" });
  const [editIndex, setEditIndex] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false); 
  const [disabledDeleteButtons, setDisabledDeleteButtons] = useState({});

  useEffect(() => {
    if (dossierData && !dossierData.error) {
      const upcoming = (dossierData.upcomingEvents || []).map(event => ({
        id: event.id,
        date: formatISO(parseISO(event.date)),
        status: event.status
      }));
      const past = (dossierData.pastEvents || []).map(event => ({
        id: event.id,
        date: formatISO(parseISO(event.date)),
        status: event.status
      }));

      dossierData.milestones.forEach(milestone => {
        const milestoneDate = parseISO(milestone.date);
        const formattedMilestoneDate = formatISO(milestoneDate); 
        if (milestoneDate > new Date()) {
          upcoming.push({ id: milestone.id, date: formattedMilestoneDate, status: milestone.summary });
        } else {
          past.push({ id: milestone.id, date: formattedMilestoneDate, status: milestone.summary });
        }
      });

      upcoming.sort((a, b) => a.date - b.date);
      past.sort((a, b) => b.date - a.date);

      setEvents({ upcoming, past });
    }
  }, [dossierData]);

  const handleOpenDialog = (index = null, type = "upcoming", isAdding = true) => {
    console.log("Opening dialog with type:", type);
    if (!isAdding && index !== null) {
      const event = events[type][index];
      setNewEvent({
        id: event.id,
        date: format(parseISO(event.date), 'yyyy-MM-dd'),
        status: event.status,
        type: type
      });
      setEditIndex(index);
      setIsSaveDisabled(false);
    } else {
      setNewEvent({ date: "", status: "", type: type });
      setEditIndex(null);
      setIsSaveDisabled(false);
    }
    setIsAdding(isAdding);
    setDialogOpen(true);
  };
  

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };


  const handleDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    const today = new Date();
    const eventType = selectedDate > today ? "upcoming" : "past";
  
    setNewEvent({
      ...newEvent,
      date: e.target.value,
      type: eventType,
    });

    console.log("Selected date type:", eventType);
  };
  

  const handleSaveEvent = () => {

    console.log("Saving event with type:", newEvent.type);
    if (!newEvent.type) {
      toast.error("Event type is undefined");
      return;
    }
  
    const eventDate = new Date(newEvent.date);
    const today = new Date();
  
    if (newEvent.type === "upcoming" && eventDate <= today) {
      toast.error("Upcoming events must have a future date");
      return;
    }
  
    if (newEvent.type === "past" && eventDate >= today) {
      toast.error("Past events must have a past date");
      return;
    }
  
    const formattedDate = formatISO(eventDate);
  
    const updatedEvents = { ...events };
    const newEventData = {
      id: newEvent.id,
      date: formattedDate,
      status: newEvent.status,
    };
  
    if (!updatedEvents[newEvent.type]) {
      updatedEvents[newEvent.type] = [];
    }
  
    if (editIndex !== null) {
      if (editIndex >= 0 && editIndex < updatedEvents[newEvent.type].length) {
        updatedEvents[newEvent.type][editIndex] = newEventData;
      } else {
        toast.error("Invalid event index");
        return;
      }
    } else {
      updatedEvents[newEvent.type].push(newEventData);
    }

    setEvents(updatedEvents);
    onEventChange(updatedEvents);
    setIsSaveDisabled(true);
    handleCloseDialog();
  };
  

  /* const handleDeleteEvent = async (index, type) => {
    const event = events[type][index];
    const updatedEvents = { ...events };
    updatedEvents[type] = updatedEvents[type].filter((_, i) => i !== index);

    try {
      setDisabledDeleteButtons(prev => ({ ...prev, [index]: true })); 
      await deleteKeyDetail({ id: { event: event.id } });

      setEvents(updatedEvents);
      onEventChange(updatedEvents);

      refreshDossierData();
      toast.success("Event deleted successfully");
    } catch (error) {
      toast.error("Failed to delete event");
      setDisabledDeleteButtons(prev => ({ ...prev, [index]: false }));
    }
  }; */


  const handleDeleteEvent = async (index, type) => {
    const event = events[type][index];
    const updatedEvents = { ...events };
    updatedEvents[type] = updatedEvents[type].filter((_, i) => i !== index);
  
    try {
      setDisabledDeleteButtons(prev => ({ ...prev, [index]: true }));
      
      await deleteKeyDetail({ id: { event: event.id } });
  
      setEvents(updatedEvents);
      onEventChange(updatedEvents);
  
      refreshDossierData();

      setDisabledDeleteButtons(prev => {
        const updatedState = { ...prev };
        delete updatedState[index]; 
        return updatedState;
      });

      toast.success("Event deleted successfully");
    } catch (error) {
      toast.error("Failed to delete event");
      
      setDisabledDeleteButtons(prev => ({ ...prev, [index]: false }));
    }
  };
  

  const renderEvent = (event, index, type) => (
    <TimelineItem key={index}>
      <TimelineOppositeContent color="textSecondary">
        {format(parseISO(event.date), 'dd MMM yyyy')}
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineDot />
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent>
        <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
          {typeof event.status === 'string' ? (
            <div dangerouslySetInnerHTML={{ __html: event.status }} />
          ) : (
            event.status
          )}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <IconButton onClick={() => handleOpenDialog(index, type, false)} color="primary">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDeleteEvent(index, type)} color="error"
            disabled={disabledDeleteButtons[index]} >
            <DeleteIcon />
          </IconButton>
        </Box>
      </TimelineContent>
    </TimelineItem>
  );

  if (!dossierData) {
    return null;
  }

  return (
    <Box>
      <ToastContainer />
      <Box sx={{ marginTop: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", color: "#00A0B2" }}>
          Add New Event
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog(null, "upcoming", true)}>
          Add Event
        </Button>
      </Box>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "white" }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", color: "#00A0B2" }}>
                      Past Events
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "white" }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", color: "#00A0B2" }}>
                      Upcoming Events
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <Timeline position="right">
                      {events.past.map((event, index) => renderEvent(event, index, "past"))}
                    </Timeline>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <Timeline position="right">
                      {events.upcoming.map((event, index) => renderEvent(event, index, "upcoming"))}
                    </Timeline>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{isAdding ? "Add Event" : "Edit Event"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Date"
                type="date"
                value={newEvent.date}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <ReactQuill
                value={newEvent.status}
                onChange={(value) => setNewEvent({ ...newEvent, status: value })}
                placeholder="Event status"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
          disabled={isSaveDisabled}
          onClick={handleSaveEvent} color="primary" variant="contained">
            Save
          </Button>
          <Button onClick={handleCloseDialog} color="error" variant="contained">Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Events;
